// frontend/src/utils/autoUpdate.js
//
// Auto-update device kalau ada deploy baru — TANPA user harus clear cache manual.
// Cek /version.json berkala (jam 00:00, saat device di-fokus, tiap pindah menu,
// dan tiap 10 menit). Kalau build-ID server beda -> clear cache SW + reload.
// Kalau sama -> diam.

import { bagianWIB } from './format';

const VERSION_URL = '/version.json';
const FOCUS_THROTTLE_MS = 60 * 1000;
// Dulu 1 jam. Terlalu lama: chunk halaman (App.jsx memecah ~110 halaman lewat
// lazy()) namanya berubah tiap deploy dan file lama hilang dari server, jadi
// device yang masih menjalankan build lama akan gagal membuka menu yang belum
// pernah dibuka — sampai sejam. 10 menit mempersempit jendela itu.
const POLL_INTERVAL_MS = 10 * 60 * 1000;
// Pindah menu juga memicu cek (dipanggil dari App.jsx), tapi jangan tiap klik.
const ROUTE_THROTTLE_MS = 2 * 60 * 1000;

// Build ID yang BENAR-BENAR sedang berjalan, ditanam saat build (scripts/build.js).
// Ini patokan yang sahih. Dulu patokannya diambil dari hasil fetch version.json
// yang PERTAMA — dan itu keliru untuk device yang baru dibuka SETELAH deploy:
// kode yang jalan masih versi lama dari cache service worker, tapi version.json
// (diambil no-store, selalu segar) sudah melaporkan versi baru. Device lalu
// mencatat versi baru itu sebagai patokan, merasa mutakhir, dan tidak pernah
// reload sendiri — user terpaksa menunggu banner SW lalu menekan Update manual.
const LOCAL_BUILD = process.env.REACT_APP_BUILD_ID || null;

// Saat `npm start` (dev) LOCAL_BUILD kosong → jatuh ke perilaku lama: patokan
// diambil dari fetch pertama. Sengaja, supaya dev server tidak reload sendiri.
let LOADED_BUILD = LOCAL_BUILD;
let lastFocusCheck = 0;
let lastRouteCheck = 0;
let reloading = false;

// Buang seluruh cache lalu reload.
//
// Reload biasa TIDAK cukup: service-worker.js mem-precache '/' dan '/index.html'
// dan route precache didaftarkan paling awal, jadi navigasi dilayani cache-first
// dari build lama — index.html-nya tidak pernah berganti dan device tetap
// menunjuk chunk yang sudah dihapus di server. Karena itu cache dihapus dulu.
// Dipakai auto-update DAN lazyRetry.js saat chunk halaman gagal dimuat.
export async function muatUlangBersih() {
  if (reloading) return;
  reloading = true;

  try {
    if (window.caches) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {}

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => {})));
    }
  } catch (e) {}

  window.location.reload();
}

async function fetchServerBuild() {
  // Saat `npm start` version.json TIDAK ADA: berkas itu baru dibuat oleh
  // `npm run build` (scripts/gen-version.js) ke folder build/, bukan public/.
  // Jadi fetch-nya tidak ketemu sebagai berkas statis, diteruskan CRA ke
  // "proxy" di package.json (:5000 — jatah Bersihin), dan meledak
  // ECONNREFUSED. Bukan cuma berisik: dinding merah itu menyamarkan galat
  // proxy yang sungguhan. Lagipula percuma — tanpa REACT_APP_BUILD_ID,
  // check() cuma mencatat hasil fetch pertama dan memang sengaja tidak
  // pernah reload sendiri di dev.
  //
  // Penjaganya di SINI, bukan cuma di initAutoUpdate: cekSaatPindahMenu()
  // dipanggil App.jsx tiap ganti route dan masuk ke check() langsung.
  // null di sini sudah ditangani kedua pemanggil (check & adaBuildBaru).
  if (!LOCAL_BUILD) return null;
  const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('version.json ' + res.status);
  const data = await res.json();
  return data.build || null;
}

async function applyUpdateAndReload(targetBuild) {
  if (reloading) return;
  try {
    const k = 'ft_au_reloaded_to';
    if (sessionStorage.getItem(k) === targetBuild) return;
    sessionStorage.setItem(k, targetBuild);
  } catch (e) {}

  await muatUlangBersih();
}

async function check() {
  if (reloading || !navigator.onLine) return;
  try {
    const serverBuild = await fetchServerBuild();
    if (!serverBuild) return;
    if (LOADED_BUILD == null) { LOADED_BUILD = serverBuild; return; }
    if (serverBuild !== LOADED_BUILD) await applyUpdateAndReload(serverBuild);
  } catch (e) {}
}

// Dipanggil App.jsx tiap pindah route. Pindah menu adalah momen paling rawan:
// di situlah chunk halaman baru diminta, jadi lebih baik ketahuan ada build
// baru SEBELUM chunk lama diminta ke server.
export function cekSaatPindahMenu() {
  const now = Date.now();
  if (now - lastRouteCheck < ROUTE_THROTTLE_MS) return;
  lastRouteCheck = now;
  check();
}

// true kalau build di server sudah beda dengan yang sedang jalan di device.
// Dipakai lazyRetry.js untuk membedakan "chunk hilang karena deploy baru"
// (perlu reload bersih) dari "jaringan lagi ngadat" (jangan buang cache).
// null = tidak bisa memastikan (version.json tak terjangkau).
export async function adaBuildBaru() {
  try {
    const serverBuild = await fetchServerBuild();
    if (!serverBuild || LOADED_BUILD == null) return null;
    return serverBuild !== LOADED_BUILD;
  } catch (e) {
    return null;
  }
}

// Tengah malam WIB (+5 detik), bukan tengah malam jam perangkat.
function msUntilNextMidnight() {
  const p = bagianWIB();
  const sejakTengahMalam = ((p.jam * 60 + p.menit) * 60 + p.detik) * 1000;
  return 24 * 60 * 60 * 1000 - sejakTengahMalam + 5000;
}

function scheduleMidnight() {
  setTimeout(() => {
    check();
    setInterval(() => check(), 24 * 60 * 60 * 1000);
  }, msUntilNextMidnight());
}

export function initAutoUpdate() {
  if (typeof window === 'undefined') return;
  // Dev: tidak ada version.json — jangan pasang timer & listener sama sekali.
  // Alasan lengkapnya di fetchServerBuild().
  if (!LOCAL_BUILD) return;
  check();
  setInterval(() => check(), POLL_INTERVAL_MS);
  const onFocus = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastFocusCheck < FOCUS_THROTTLE_MS) return;
    lastFocusCheck = now;
    check();
  };
  document.addEventListener('visibilitychange', onFocus);
  window.addEventListener('focus', onFocus);
  scheduleMidnight();
}
